import { WritableSignal, signal } from '@angular/core';
import { Auction, Id, Lot, LotProcess, AutoBid, FinishedAuction, CurrentOrganizationOffer } from '../domain/models';

export const isNotStarted = (m: LotProcess) => m.kind === 'NotStartedAuction';
export const isActive     = (m: LotProcess) => m.kind === 'ActiveAuction';
export const isFinished   = (m: LotProcess) => m.kind === 'FinishedAuction';

export interface LotItemRef {
    readonly id: Id;
    readonly model: WritableSignal<LotProcess>;
}

export class LotProcessStore {
    private readonly lotItemRefsById = new Map<Id, LotItemRef>();

    get itemRefsById() {
        return this.lotItemRefsById;
    }

    upsertLots(lots: readonly Lot[]): void {
        for (const lot of lots) {
            const existing = this.refOf(lot.id);
            if (existing) {
                continue;
            }

            const model = signal<LotProcess>({ kind: 'NotStartedAuction', lot, autobid: null });

            const ref: LotItemRef = {
                id: lot.id,
                model
            };

            this.lotItemRefsById.set(lot.id, ref);
        }
    }

    activateAuction(auction: Auction): void {
        const ref = this.refOf(auction.lotId);
        if (!ref) return;

        const model = ref.model();

        if (isFinished(model) || isActive(model)) return;

        ref.model.set({ kind: 'ActiveAuction', lot: model.lot, auction: auction, autobid: model.autobid, currentOrganizationOffer: null });
    }

    finishAuction(auction: Auction): void {
        const ref = this.refOf(auction.lotId);
        if (!ref) return;

        const model = ref.model();

        if (isFinished(model)) return;

        ref.model.set(<FinishedAuction>{ kind: 'FinishedAuction', lot: model.lot, auction, currentOrganizationOffer: model.kind === 'NotStartedAuction' ? null : model.currentOrganizationOffer });
    }

    patchActiveAuction(lotId: Id, patch: Partial<Auction>): void {
        const ref = this.refOf(lotId);
        if (!ref) return;

        const model = ref.model();

        if (!isActive(model)) return;

        const nextAuction: Auction = { ...model.auction, ...patch };

        ref.model.set({ kind: 'ActiveAuction', lot: ref.model().lot, auction: nextAuction, autobid: model.autobid, currentOrganizationOffer: model.currentOrganizationOffer });
    }

    setAutoBid(lotId: Id, autobid: AutoBid): void {
        const ref = this.refOf(lotId);
        if (!ref) return;

        const model = ref.model();
        if (isFinished(model)) return;

        ref.model.set({ ...model, autobid });
    }

    setCurrentOffer(lotId: Id, currentOffer: CurrentOrganizationOffer): void {
        const ref = this.refOf(lotId);
        if (!ref) return;

        const model = ref.model();
        if (isNotStarted(model)) return;

        ref.model.set({ ...model, currentOrganizationOffer: currentOffer });
    }

    removeAutobid(lotId: Id): void {
        const ref = this.refOf(lotId);
        if (!ref) return;

        const model = ref.model();
        if (isFinished(model)) return;

        ref.model.set({ ...model, autobid: null });
    }

    clear(): void {
        this.lotItemRefsById.clear();
    }

    private refOf(id: Id): LotItemRef | undefined {
        return this.lotItemRefsById.get(id);
    }
}
